import type { PolygonWithHoles, Ring, Vec2 } from '../../geo/types';

// Grid cells for looking points up; far larger than any tolerance they serve.
const CELL_M = 2;
// An edge is searched at samples this far apart, each over the cells within half of it.
const STEP_M = CELL_M / 2;

// Cell indices are offset into one number each, so a key is a number and not a string.
const OFFSET = 2 ** 20;
const SPAN = 2 ** 21;
// An edge whose cells, widened by the reach, number at most this many searches its whole box.
const BOX_CELLS = 16;

interface PointGrid {
  readonly cells: Map<number, Vec2[]>;
}

function cellKey(i: number, j: number): number {
  return (i + OFFSET) * SPAN + (j + OFFSET);
}

function cellOf(v: number): number {
  return Math.floor(v / CELL_M);
}

function emptyGrid(): PointGrid {
  return { cells: new Map() };
}

// The grid is private to its builder, so its cells grow in place.
function addTo(grid: PointGrid, p: Vec2): void {
  const key = cellKey(cellOf(p[0]), cellOf(p[1]));
  const cell = grid.cells.get(key);
  if (cell) cell.push(p);
  else grid.cells.set(key, [p]);
}

function eachIn(
  grids: readonly PointGrid[],
  keys: Iterable<number>,
  visit: (p: Vec2) => void
): void {
  for (const key of keys) {
    for (const grid of grids) {
      const cell = grid.cells.get(key);
      if (cell) cell.forEach(visit);
    }
  }
}

function boxKeys(minI: number, maxI: number, minJ: number, maxJ: number): number[] {
  const keys: number[] = [];
  for (let i = minI; i <= maxI; i++) {
    for (let j = minJ; j <= maxJ; j++) keys.push(cellKey(i, j));
  }
  return keys;
}

// The first point of the grid within the tolerance of p, if any.
function firstWithin(grid: PointGrid, p: Vec2, tolerance: number): Vec2 | undefined {
  for (let i = cellOf(p[0] - tolerance); i <= cellOf(p[0] + tolerance); i++) {
    for (let j = cellOf(p[1] - tolerance); j <= cellOf(p[1] + tolerance); j++) {
      const same = grid.cells
        .get(cellKey(i, j))
        ?.find((q) => Math.hypot(q[0] - p[0], q[1] - p[1]) <= tolerance);
      if (same) return same;
    }
  }
  return undefined;
}

// The cells within the reach of samples STEP_M apart along a → b.
function steppedKeys(a: Vec2, b: Vec2, reach: number): Set<number> {
  const steps = Math.max(1, Math.ceil(Math.hypot(b[0] - a[0], b[1] - a[1]) / STEP_M));
  const keys = new Set<number>();
  for (let k = 0; k <= steps; k++) {
    const [x, y] = [a[0] + ((b[0] - a[0]) * k) / steps, a[1] + ((b[1] - a[1]) * k) / steps];
    for (let i = cellOf(x - reach); i <= cellOf(x + reach); i++) {
      for (let j = cellOf(y - reach); j <= cellOf(y + reach); j++) keys.add(cellKey(i, j));
    }
  }
  return keys;
}

/* The cells within reach of an edge. A short edge searches the cells of its box; a long one is
   walked in steps along it, so an edge across the city searches only the cells it passes, not its
   whole box. */
function cellsAlong(a: Vec2, b: Vec2, tolerance: number): Iterable<number> {
  const reach = STEP_M / 2 + tolerance;
  const [minI, maxI] = [cellOf(Math.min(a[0], b[0]) - reach), cellOf(Math.max(a[0], b[0]) + reach)];
  const [minJ, maxJ] = [cellOf(Math.min(a[1], b[1]) - reach), cellOf(Math.max(a[1], b[1]) + reach)];
  const small = (maxI - minI + 1) * (maxJ - minJ + 1) <= BOX_CELLS;
  return small ? boxKeys(minI, maxI, minJ, maxJ) : steppedKeys(a, b, reach);
}

/* A fixed point within the tolerance of p stands for it, else the first piece point seen there, so
   points Clipper rounded apart from one crossing become one and fixed points never move. */
function welder(
  tolerance: number,
  fixed: PointGrid
): { readonly grid: PointGrid; readonly weld: (p: Vec2) => Vec2 } {
  const grid = emptyGrid();
  const weld = (p: Vec2): Vec2 => {
    const same = firstWithin(fixed, p, tolerance) ?? firstWithin(grid, p, tolerance);
    if (same) return same;
    addTo(grid, p);
    return p;
  };
  return { grid, weld };
}

// Where along a → b the point lies, if it lies within the tolerance of the edge between its ends.
function placeOnEdge(a: Vec2, b: Vec2, p: Vec2, tolerance: number): number | null {
  if (p === a || p === b) return null;
  const [dx, dy] = [b[0] - a[0], b[1] - a[1]];
  const length2 = dx * dx + dy * dy;
  if (length2 === 0) return null;
  const t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / length2;
  if (t <= 0 || t >= 1) return null;
  const off = Math.abs((p[0] - a[0]) * dy - (p[1] - a[1]) * dx) / Math.sqrt(length2);
  const ends = Math.min(t, 1 - t) * Math.sqrt(length2);
  return off <= tolerance && ends > tolerance ? t : null;
}

// A ring that runs out to a vertex and straight back, where welding closed a sliver, loses the spur.
function withoutSpikes(ring: Ring): Ring {
  let out = ring;
  for (let k = 0; k < out.length && out.length >= 3;) {
    const before = out[(k + out.length - 1) % out.length]!;
    const after = out[(k + 1) % out.length]!;
    if (before !== after) {
      k++;
      continue;
    }
    const drop = new Set([k, (k + 1) % out.length]);
    out = out.filter((_, j) => !drop.has(j));
    k = Math.max(0, k - 1);
  }
  return out;
}

// The points within the tolerance of a → b, in order along it.
function pointsOnEdge(grids: readonly PointGrid[], a: Vec2, b: Vec2, tolerance: number): Vec2[] {
  const hits: { readonly p: Vec2; readonly t: number }[] = [];
  eachIn(grids, cellsAlong(a, b, tolerance), (p) => {
    const t = placeOnEdge(a, b, p, tolerance);
    if (t !== null) hits.push({ p, t });
  });
  return hits.sort((x, y) => x.t - y.t).map((hit) => hit.p);
}

function withPointsOnEdges(ring: Ring, grids: readonly PointGrid[], tolerance: number): Ring {
  const out: Vec2[] = [];
  ring.forEach((a, k) => {
    out.push(a, ...pointsOnEdge(grids, a, ring[(k + 1) % ring.length]!, tolerance));
  });
  return out.filter((p, k) => p !== out[(k + 1) % out.length]);
}

function gridOf(polygons: readonly PolygonWithHoles[]): PointGrid {
  const grid = emptyGrid();
  for (const polygon of polygons) {
    for (const ring of [polygon.outer, ...polygon.holes]) {
      for (const p of ring) addTo(grid, p);
    }
  }
  return grid;
}

// A fixed piece keeps every vertex it has and takes the pieces' vertices on its edges; untouched,
// it comes back as the same object.
function fittedTo(polygon: PolygonWithHoles, grid: PointGrid, tolerance: number): PolygonWithHoles {
  const fit = (ring: Ring): Ring => {
    const out = withPointsOnEdges(ring, [grid], tolerance);
    return out.length === ring.length && out.every((p, k) => p === ring[k]) ? ring : out;
  };
  const outer = fit(polygon.outer);
  const holes = polygon.holes.map(fit);
  const same = outer === polygon.outer && holes.every((hole, k) => hole === polygon.holes[k]);
  return same ? polygon : { outer, holes };
}

export interface Conformed {
  readonly pieces: readonly PolygonWithHoles[];
  readonly fixed: readonly PolygonWithHoles[];
}

/**
 * Pieces conformed to one another as `conformed` does, and to ground that stays where it is: a
 * piece vertex within `tolerance` of a fixed vertex moves onto it, a piece takes every fixed vertex
 * within `tolerance` of its edges, and each fixed piece keeps its own vertices and takes the pieces'
 * vertices on its edges, but never another fixed piece's.
 */
export function conformedTo(
  pieces: readonly PolygonWithHoles[],
  fixed: readonly PolygonWithHoles[],
  tolerance: number
): Conformed {
  const fixedGrid = gridOf(fixed);
  const { grid, weld } = welder(tolerance, fixedGrid);
  const welded = pieces.map((piece) => ({
    outer: piece.outer.map(weld),
    holes: piece.holes.map((hole) => hole.map(weld)),
  }));
  const grids = [fixedGrid, grid];
  return {
    pieces: welded.map((piece) => ({
      outer: withoutSpikes(withPointsOnEdges(piece.outer, grids, tolerance)),
      holes: piece.holes
        .map((hole) => withoutSpikes(withPointsOnEdges(hole, grids, tolerance)))
        .filter((hole) => hole.length >= 3),
    })),
    fixed: fixed.map((polygon) => fittedTo(polygon, grid, tolerance)),
  };
}

/**
 * Pieces that meet along a line two boolean operations computed apart, each rounding its own
 * crossings to Clipper's millimetre, share their vertices along it: points within `tolerance` of
 * each other become one, and a piece takes every other piece's vertex within `tolerance` of its
 * edges. The pieces then meet edge to edge, with no sliver between them or over each other. A ring
 * that closes to a line comes back with fewer than three vertices.
 */
export function conformed(
  pieces: readonly PolygonWithHoles[],
  tolerance: number
): readonly PolygonWithHoles[] {
  return conformedTo(pieces, [], tolerance).pieces;
}
